import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import type { Signature } from '@/types/email';
import { Check, FileSignature } from 'lucide-react';

interface SignaturePickerProps {
  signatures: Signature[];
  selectedSignatureId: string | null;
  onSelect: (value: string) => void;
  onManage?: () => void;
}

export function SignaturePicker({ signatures, selectedSignatureId, onSelect, onManage }: SignaturePickerProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              tabIndex={-1}
              className={cn('h-7 w-7 p-0', selectedSignatureId && 'text-primary')}
              disabled={signatures.length === 0}
            >
              <FileSignature className="h-3.5 w-3.5" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">Signature</TooltipContent>
      </Tooltip>
      <PopoverContent className="w-64 p-2" align="start" side="top">
        <div className="space-y-1">
          <Button
            type="button"
            variant="ghost"
            className={cn(
              'w-full justify-between px-2 py-1.5 h-auto text-sm',
              !selectedSignatureId && 'bg-accent text-accent-foreground'
            )}
            onClick={() => {
              onSelect('none');
              setOpen(false);
            }}
          >
            <span>No signature</span>
            {!selectedSignatureId && <Check className="h-4 w-4" />}
          </Button>
          {signatures.map((signature) => (
            <Button
              key={signature.id}
              type="button"
              variant="ghost"
              className={cn(
                'w-full justify-between px-2 py-1.5 h-auto text-sm',
                selectedSignatureId === signature.id && 'bg-accent text-accent-foreground'
              )}
              onClick={() => {
                onSelect(signature.id);
                setOpen(false);
              }}
            >
              <span>{signature.name}</span>
              {selectedSignatureId === signature.id && <Check className="h-4 w-4" />}
            </Button>
          ))}
          {onManage && signatures.length > 0 && (
            <>
              <Separator className="my-1" />
              <Button
                type="button"
                variant="ghost"
                className="w-full justify-start px-2 py-1.5 h-auto text-xs text-muted-foreground hover:text-foreground"
                onClick={() => {
                  setOpen(false);
                  onManage();
                }}
              >
                Manage signatures
              </Button>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
